import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash';
import { MenuItem, MessageService } from 'primeng/api';
import { Subject, combineLatest, distinctUntilChanged, map, takeUntil } from 'rxjs';

import { environment } from './core/core.module';
import { MobileMenuService } from './mobile-menu.service';
import { ThemeFacade } from './repositories/theme/store/theme.facade';
import { IHelpItem } from './repositories/theme/store/theme.models';
import { FileService } from './shared/services';
import { WalkthroughService } from './walkthrough.service';

@Injectable({
  providedIn: 'root'
})
export class HelpMenuService implements OnDestroy {
  public helpMenuItems$ = new Subject<MenuItem[]>();

  private readonly destroyed$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private themeFacade: ThemeFacade,
    private translateService: TranslateService,
    private mobileMenuService: MobileMenuService,
    private walkthroughService: WalkthroughService,
    private fileService: FileService,
    private messageService: MessageService
  ) {}

  public init() {
    combineLatest([this.themeFacade.helpInformation$, this.walkthroughService.walkthroughForCurrentRoute$])
      .pipe(
        map(([helpInformation, currentWalkthrough]) => ({ helpInformation, currentWalkthrough })),
        distinctUntilChanged(isEqual),
        map(({ helpInformation, currentWalkthrough }) => {
          const items =
            helpInformation?.map((item: IHelpItem) => {
              if (item.downloadPath) {
                return {
                  label: item.name,
                  command: () => {
                    this.mobileMenuService.mobileMenuOpen = false;

                    try {
                      this.fileService.downloadFile(`${environment.BACKEND_URL}${item.downloadPath}`, item.name);
                    } catch (err) {
                      console.error(err);
                      this.messageService.add({
                        severity: 'error',
                        detail: this.translateService.instant('error.document.download'),
                        life: 5000
                      });
                    }
                  }
                };
              }

              return {
                label: item.name,
                url: item.url
              };
            }) || [];

          if (currentWalkthrough) {
            items?.push({
              label: this.translateService.instant('walkthrough.title'),
              command: () => {
                this.walkthroughService.showWalkthroughByMenu();
              }
            });
          }

          return items;
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((items) => {
        this.mobileMenuService.setMobileMenuItemAtIndex(
          {
            label: this.translateService.instant('information.help.title'),
            items: items,
            icon: 'fa-regular fa-question'
          },
          5
        );

        this.helpMenuItems$.next(items);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
